import { useState } from "react";
import { Link } from "react-router-dom";
import { useCompanies, useAddCompany, useDeleteCompany } from "@/hooks/use-companies";
import { ScoreBadge } from "@/components/shared/score-badge";
import { formatCnpj, getRiskBg } from "@/lib/utils";
import { Plus, Building2, AlertTriangle, Shield, Activity, X, Trash2 } from "lucide-react";

export function DashboardPage() {
  const [showAdd, setShowAdd] = useState(false);
  const [cnpjInput, setCnpjInput] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { data, isLoading } = useCompanies();
  const addCompany = useAddCompany();
  const removeCompany = useDeleteCompany();

  const companies = data?.companies || [];
  const total = data?.total || 0;
  const avgScore = companies.length > 0
    ? Math.round(companies.filter((c) => c.score_overall !== null).reduce((s, c) => s + (c.score_overall || 0), 0) / Math.max(1, companies.filter((c) => c.score_overall !== null).length))
    : 0;
  const criticalCount = companies.filter((c) => c.risk_level === "CRITICO" || c.risk_level === "ALTO").length;

  const handleAdd = () => {
    if (cnpjInput.trim()) {
      addCompany.mutate({ cnpj: cnpjInput.trim() }, {
        onSuccess: () => { setCnpjInput(""); setShowAdd(false); },
      });
    }
  };

  const kpis = [
    { label: "Companies", value: String(total), icon: Building2 },
    { label: "Avg. Score", value: avgScore > 0 ? String(avgScore) : "--", icon: Activity },
    { label: "High Risk", value: String(criticalCount), icon: AlertTriangle },
    { label: "Monitored", value: String(companies.filter((c) => c.monitoring_enabled).length), icon: Shield },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Portfolio</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor fiscal health across your target companies</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800"
        >
          <Plus className="w-4 h-4" /> Add CNPJ
        </button>
      </div>

      {/* Add CNPJ dialog */}
      {showAdd && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={cnpjInput}
              onChange={(e) => setCnpjInput(e.target.value)}
              placeholder="Enter CNPJ (e.g., 12.345.678/0001-90)"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <button onClick={handleAdd} disabled={addCompany.isPending} className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-50">
              {addCompany.isPending ? "Adding..." : "Analyze"}
            </button>
            <button onClick={() => setShowAdd(false)} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        {kpis.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-500">{label}</span>
              <Icon className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      {/* Companies table */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-sm font-medium text-slate-900">Monitored Companies</h2>
        </div>
        {isLoading ? (
          <div className="px-6 py-16 text-center text-slate-400">Loading...</div>
        ) : companies.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-1">No companies yet</p>
            <p className="text-xs text-slate-400">Add your first CNPJ to start analyzing fiscal health</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">CNPJ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Last Analysis</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {companies.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 group">
                  <td className="px-6 py-3">
                    <Link to={`/company/${c.id}`} className="text-sm font-medium text-slate-900 hover:text-blue-600">
                      {c.razao_social || "Pending analysis..."}
                    </Link>
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-500 font-mono">{formatCnpj(c.cnpj)}</td>
                  <td className="px-6 py-3"><ScoreBadge score={c.score_overall} riskLevel={c.risk_level} /></td>
                  <td className="px-6 py-3 text-sm text-slate-500">{c.situacao_cadastral || "--"}</td>
                  <td className="px-6 py-3 text-xs text-slate-400">
                    {c.last_analysis_at ? new Date(c.last_analysis_at).toLocaleDateString("pt-BR") : "Never"}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteId(c.id); }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                      title="Remove company"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900 mb-2">Remove company</h3>
            <p className="text-sm text-slate-500 mb-5">
              This will delete all analysis data, scores, and reports for this company. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  removeCompany.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
                }}
                disabled={removeCompany.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {removeCompany.isPending ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
